'use client';

import { c as _c } from "react/compiler-runtime";
import { Suspense } from 'react';
import { Column, TransformedNewsItem, ChildOrder, Section } from '@/lib/types/pageBuilder';
import SectionHeading from './SectionHeading';
import TabBarSection, { DynamicTabInfo } from './TabBarSection';
import SliderWidget from './SliderWidget';
import { MultiComponentRenderer } from './ComponentRenderer';
import RowRenderer from './RowRenderer';
import { getSectionComponent } from '@/lib/sectionRegistry';
import type { IncludeSectionApiData } from '@/lib/pageUtils';
import { normalizeApiEndpoint } from '@/lib/apiUtils';
import { getStickyStyles } from '@/lib/componentutills';
interface ColumnContentRendererProps {
  column: Column;
  collectionsData: Map<string, TransformedNewsItem[]>;
  dynamicTabsMap?: Map<string, DynamicTabInfo[]>;
  isLastColumn?: boolean;
  includeSectionsData?: Map<string, Section>;
  includeSectionApiData?: IncludeSectionApiData;
  skipOuterWrapper?: boolean; // When true, render content without outer grid column wrapper
  parentColumnData?: TransformedNewsItem[]; // Data inherited from parent column's data_binding
  dataStartIndex?: number; // Start index for sequential data consumption
  /** Mark first component's image as priority for LCP optimization */
  priority?: boolean;
}

/**
 * Calculate how many items a component will consume based on its data_config
 * When mode is 'loop' without loop_count, uses a large number to consume all available items
 */
function getComponentItemCount(component: {
  data_config?: {
    mode?: string;
    loop_count?: number;
  };
  slot_id?: string;
  provider?: string;
  widget_type?: string;
}): number {
  // Ad widgets don't consume data items
  if (component.slot_id || component.provider || component.widget_type === 'custom_ad' || component.widget_type === 'html' || component.widget_type === 'iframe') {
    return 0;
  }
  if (!component.data_config) return 1;
  if (component.data_config.mode === 'loop') {
    return component.data_config.loop_count !== undefined ? component.data_config.loop_count : 999;
  }
  return 1; // single mode consumes 1 item
}

/**
 * Calculate total items consumed by all components in a column
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 */
function getColumnItemCount(column: Column): number {
  let count = 0;

  // Count components
  if (column.components) {
    for (const comp of column.components) {
      count += getComponentItemCount(comp);
    }
  }

  // Count nested columns
  if (column.columns) {
    for (const nestedCol of column.columns) {
      count += getColumnItemCount(nestedCol);
    }
  }

  // Count nested rows
  if (column.rows) {
    for (const row of column.rows) {
      count += getRowItemCount(row);
    }
  }
  return count;
}

/**
 * Calculate total items consumed by all columns in a row
 * Based on component loop_count (not data_binding.limit - that's for API calls)
 */
function getRowItemCount(row: {
  columns?: Column[];
}): number {
  let count = 0;
  if (row.columns) {
    for (const col of row.columns) {
      count += getColumnItemCount(col);
    }
  }
  return count;
}

// Map of Tailwind classes for grid columns
const colSpanMap: Record<number, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
  7: 'col-span-7',
  8: 'col-span-8',
  9: 'col-span-9',
  10: 'col-span-10',
  11: 'col-span-11',
  12: 'col-span-12'
};
const mdColSpanMap: Record<number, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
  4: 'md:col-span-4',
  5: 'md:col-span-5',
  6: 'md:col-span-6',
  7: 'md:col-span-7',
  8: 'md:col-span-8',
  9: 'md:col-span-9',
  10: 'md:col-span-10',
  11: 'md:col-span-11',
  12: 'md:col-span-12'
};
const lgColSpanMap: Record<number, string> = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4',
  5: 'lg:col-span-5',
  6: 'lg:col-span-6',
  7: 'lg:col-span-7',
  8: 'lg:col-span-8',
  9: 'lg:col-span-9',
  10: 'lg:col-span-10',
  11: 'lg:col-span-11',
  12: 'lg:col-span-12'
};
export default function ColumnContentRenderer(t0) {
  const $ = _c(72);
  const {
    column,
    collectionsData,
    dynamicTabsMap,
    includeSectionsData,
    includeSectionApiData,
    skipOuterWrapper: t1,
    parentColumnData,
    dataStartIndex: t2,
    priority: t3
  } = t0;
  const skipOuterWrapper = t1 === undefined ? false : t1;
  const dataStartIndex = t2 === undefined ? 0 : t2;
  const priority = t3 === undefined ? false : t3;
  if (column.hidden) {
    return null;
  }
  const rawDataBindingKey = column.data_binding?.api_endpoint || column.data_binding?.collection_id;
  let getAlignmentClass;
  let getWidthClass;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== collectionsData || $[1] !== column.background || $[2] !== column.backgroundImage || $[3] !== column.children_order || $[4] !== column.columns || $[5] !== column.components || $[6] !== column.heading || $[7] !== column.horizontalScroll || $[8] !== column.id || $[9] !== column.includeSection || $[10] !== column.justifyContent || $[11] !== column.pagination || $[12] !== column.rows || $[13] !== column.slider || $[14] !== column.tabBar || $[15] !== dataStartIndex || $[16] !== dynamicTabsMap || $[17] !== includeSectionApiData || $[18] !== includeSectionsData || $[19] !== parentColumnData || $[20] !== priority || $[21] !== rawDataBindingKey) {
    const dataBindingKey = rawDataBindingKey ? normalizeApiEndpoint(rawDataBindingKey) : undefined;
    const hasOwnDataBinding = !!dataBindingKey;
    const rawItems = dataBindingKey ? collectionsData.get(dataBindingKey) || [] : parentColumnData || [];
    const items = rawItems.filter(_temp);
    const currentOffset = hasOwnDataBinding ? 0 : dataStartIndex;
    column.components?.length || 0;
    column.columns?.length || 0;
    column.rows?.length || 0;
    if (dataBindingKey && items.length === 0) {
      console.error(`[ColumnContentRenderer] WARNING: Column ${column.id} has data_binding "${dataBindingKey}" but 0 items! Available keys: ${Array.from(collectionsData.keys()).join(", ")}`);
    }
    const includeSectionData = column.includeSection ? includeSectionsData?.get(column.includeSection.slug) : undefined;
    getWidthClass = _temp2;
    let t10;
    if ($[30] !== column.justifyContent) {
      t10 = () => {
        if (!column.justifyContent) {
          return "";
        }
        const alignMap = {
          "flex-start": "items-start",
          "center": "items-center",
          "flex-end": "items-end",
          "space-between": "items-stretch",
          "space-around": "items-stretch",
          "space-evenly": "items-stretch"
        };
        const mdAlignMap = {
          "flex-start": "md:items-start",
          "center": "md:items-center",
          "flex-end": "md:items-end",
          "space-between": "md:items-stretch",
          "space-around": "md:items-stretch",
          "space-evenly": "md:items-stretch"
        };
        const lgAlignMap = {
          "flex-start": "lg:items-start",
          "center": "lg:items-center",
          "flex-end": "lg:items-end",
          "space-between": "lg:items-stretch",
          "space-around": "lg:items-stretch",
          "space-evenly": "lg:items-stretch"
        };
        const classes = [];
        if (column.justifyContent.mobile) {
          classes.push(alignMap[column.justifyContent.mobile] || "");
        }
        if (column.justifyContent.tablet) {
          classes.push(mdAlignMap[column.justifyContent.tablet] || "");
        }
        if (column.justifyContent.desktop) {
          classes.push(lgAlignMap[column.justifyContent.desktop] || "");
        }
        return classes.filter(Boolean).join(" ");
      };
      $[30] = column.justifyContent;
      $[31] = t10;
    } else {
      t10 = $[31];
    }
    getAlignmentClass = t10;
    const renderIncludeSection = () => {
      if (!column.includeSection) {
        return null;
      }
      const SectionComponent = getSectionComponent(column.includeSection.slug);
      if (SectionComponent) {
        const sectionSlug = column.includeSection.slug;
        let sectionProps = {};
        if (sectionSlug === "trending-tab" || sectionSlug === "recent-news") {
          sectionProps = {
            trendingStories: includeSectionApiData?.trendingStories || [],
            commentedStories: includeSectionApiData?.commentedStories || []
          };
        } else {
          if (sectionSlug === "image-video" || sectionSlug === "page-foot-section") {
            sectionProps = {
              galleries: includeSectionApiData?.galleries || [],
              videos: includeSectionApiData?.videos || []
            };
          }
        }
        return <div className="include-section" data-section-slug={column.includeSection.slug}><SectionComponent {...sectionProps} /></div>;
      }
      if (!includeSectionData) {
        return <div className="include-section-placeholder p-4 text-center text-gray-400">Section: {column.includeSection.name || column.includeSection.slug}</div>;
      }
      return <div className="include-section" data-section-slug={column.includeSection.slug}>{includeSectionData.rows.map((row, index) => <RowRenderer key={row.id} row={row} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastRow={index === includeSectionData.rows.length - 1} parentColumnData={items.length > 0 ? items : parentColumnData} />)}</div>;
    };
    const renderOrderedChildren = () => {
      if (!column.children_order || column.children_order.length === 0) {
        return null;
      }
      let runningOffset = currentOffset;
      const elements = [];
      const nestedColumnElements = [];
      if (column.horizontalScroll && column.columns && column.columns.length > 0) {
        for (const child of column.children_order) {
          if (child.type === "component") {
            const component = column.components?.find(c => c.id === child.id);
            if (component) {
              getComponentItemCount(component);
            }
          }
        }
      }
      let firstComponentRendered = false;
      for (const child_0 of column.children_order) {
        if (child_0.type === "component") {
          const component_0 = column.components?.find(c_0 => c_0.id === child_0.id);
          if (component_0) {
            const itemCount = getComponentItemCount(component_0);
            const slicedItems = items.slice(runningOffset, runningOffset + itemCount);
            const componentPriority = priority && !firstComponentRendered;
            firstComponentRendered = true;
            elements.push(<MultiComponentRenderer key={child_0.id} components={[component_0]} items={slicedItems} includeSectionApiData={includeSectionApiData} priority={componentPriority} horizontalScroll={column.horizontalScroll} />);
            runningOffset = runningOffset + itemCount;
          }
        } else {
          if (child_0.type === "nested-column") {
            const nestedColumn = column.columns?.find(c_1 => c_1.id === child_0.id);
            if (nestedColumn) {
              const itemCount_0 = getColumnItemCount(nestedColumn);
              if (column.horizontalScroll) {
                nestedColumnElements.push(<ColumnContentRenderer key={child_0.id} column={nestedColumn} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastColumn={column.columns ? column.columns.indexOf(nestedColumn) === column.columns.length - 1 : false} parentColumnData={items} dataStartIndex={runningOffset} skipOuterWrapper={true} />);
              } else {
                elements.push(<ColumnContentRenderer key={child_0.id} column={nestedColumn} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastColumn={column.columns ? column.columns.indexOf(nestedColumn) === column.columns.length - 1 : false} parentColumnData={items} dataStartIndex={runningOffset} />);
              }
              runningOffset = runningOffset + itemCount_0;
            }
          } else {
            if (child_0.type === "nested-row") {
              const nestedRow = column.rows?.find(r => r.id === child_0.id);
              if (nestedRow) {
                const itemCount_1 = getRowItemCount(nestedRow);
                elements.push(<RowRenderer key={child_0.id} row={nestedRow} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastRow={column.rows ? column.rows.indexOf(nestedRow) === column.rows.length - 1 : false} parentColumnData={items} dataStartIndex={runningOffset} />);
                runningOffset = runningOffset + itemCount_1;
              }
            } else {
              if (child_0.type === "include-section") {
                if (column.includeSection && child_0.id === column.includeSection.id) {
                  elements.push(<div key={child_0.id}>{renderIncludeSection()}</div>);
                }
              }
            }
          }
        }
      }
      if (column.horizontalScroll && nestedColumnElements.length > 0) {
        const nestedColumnIds = column.children_order.filter(_temp3).map(_temp4);
        return <>{elements.filter((_, i) => {
            const child_1 = column.children_order[i];
            return child_1?.type !== "nested-column";
          })}<div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4 lg:grid lg:grid-cols-12 lg:overflow-visible lg:mx-0 lg:px-0 lg:gap-0">{nestedColumnElements.map((el, index_0) => {
              const colId = nestedColumnIds[index_0];
              const nestedCol = column.columns?.find(c_4 => c_4.id === colId);
              const lgClass = lgColSpanMap[nestedCol?.width?.desktop || 12] || "lg:col-span-12";
              return <div key={colId || index_0} className={`flex-shrink-0 lg:!w-auto lg:flex-shrink ${lgClass}`} style={{
                width: "calc(75vw - 2rem)"
              }}>{el}</div>;
            })}</div></>;
      }
      return elements;
    };
    const renderStandardContent = () => {
      let runningOffset_0 = currentOffset;
      let componentsSlice = [];
      let componentsItemCount = 0;
      if (column.components && column.components.length > 0) {
        for (const comp of column.components) {
          componentsItemCount = componentsItemCount + getComponentItemCount(comp);
        }
        componentsSlice = items.slice(runningOffset_0, runningOffset_0 + componentsItemCount);
        runningOffset_0 = runningOffset_0 + componentsItemCount;
        runningOffset_0;
      }
      const renderNestedColumns = () => {
        if (!column.columns || column.columns.length === 0) {
          return null;
        }
        let colOffset = runningOffset_0;
        const columnElements = column.columns.map((nestedColumn_0, index_1) => {
          const itemCount_2 = getColumnItemCount(nestedColumn_0);
          const element = <ColumnContentRenderer key={nestedColumn_0.id} column={nestedColumn_0} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastColumn={index_1 === column.columns.length - 1} parentColumnData={items} dataStartIndex={colOffset} />;
          colOffset = colOffset + itemCount_2;
          colOffset;
          return element;
        });
        if (column.horizontalScroll) {
          return <div className="flex gap-4 overflow-x-auto scrollbar-hide -mx-4 px-4 lg:grid lg:grid-cols-12 lg:overflow-visible lg:mx-0 lg:px-0 lg:gap-0">{column.columns.map((nestedColumn_1, index_2) => {
              let nestedOffset = runningOffset_0;
              for (let i_0 = 0; i_0 < index_2; i_0++) {
                nestedOffset = nestedOffset + getColumnItemCount(column.columns[i_0]);
              }
              const lgClass_0 = lgColSpanMap[nestedColumn_1.width?.desktop || 12] || "lg:col-span-12";
              return <div key={nestedColumn_1.id} className={`flex-shrink-0 lg:!w-auto lg:flex-shrink ${lgClass_0}`} style={{
                width: "calc(75vw - 2rem)"
              }}><ColumnContentRenderer column={nestedColumn_1} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastColumn={index_2 === column.columns.length - 1} parentColumnData={items} dataStartIndex={nestedOffset} skipOuterWrapper={true} /></div>;
            })}</div>;
        }
        return <div className="grid grid-cols-12">{columnElements}</div>;
      };
      if (column.columns) {
        for (const col of column.columns) {
          runningOffset_0 = runningOffset_0 + getColumnItemCount(col);
          runningOffset_0;
        }
      }
      const renderNestedRows = () => {
        if (!column.rows || column.rows.length === 0) {
          return null;
        }
        let rowOffset = runningOffset_0;
        return <div className="space-y-4">{column.rows.map((nestedRow_0, index_3) => {
            const itemCount_3 = getRowItemCount(nestedRow_0);
            const element_0 = <RowRenderer key={nestedRow_0.id} row={nestedRow_0} collectionsData={collectionsData} dynamicTabsMap={dynamicTabsMap} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} isLastRow={index_3 === column.rows.length - 1} parentColumnData={items} dataStartIndex={rowOffset} />;
            rowOffset = rowOffset + itemCount_3;
            rowOffset;
            return element_0;
          })}</div>;
      };
      return <>{column.components && column.components.length > 0 && <div className="components-wrapper"><MultiComponentRenderer components={column.components} items={componentsSlice} includeSectionApiData={includeSectionApiData} priority={priority} horizontalScroll={column.horizontalScroll} /></div>}{renderNestedColumns()}{renderNestedRows()}{column.includeSection && renderIncludeSection()}</>;
    };
    if ($[32] !== column.background || $[33] !== column.backgroundImage || $[34] !== column.heading) {
      t7 = column.heading && column.heading.enabled && <div className="border border-E8E8E8"><SectionHeading heading={column.heading} background={column.background} backgroundImage={column.backgroundImage} /></div>;
      $[32] = column.background;
      $[33] = column.backgroundImage;
      $[34] = column.heading;
      $[35] = t7;
    } else {
      t7 = $[35];
    }
    if ($[36] !== collectionsData || $[37] !== column.pagination || $[38] !== column.tabBar || $[39] !== dynamicTabsMap || $[40] !== includeSectionApiData || $[41] !== includeSectionsData) {
      t8 = column.tabBar && <TabBarSection tabBar={column.tabBar} pagination={column.pagination} collectionsData={collectionsData} dynamicTabs={dynamicTabsMap?.get(column.tabBar.id)} includeSectionsData={includeSectionsData} includeSectionApiData={includeSectionApiData} stickyTabs={column.tabBar?.stickyTabs} stickyTop={column.tabBar?.stickyTop} />;
      $[36] = collectionsData;
      $[37] = column.pagination;
      $[38] = column.tabBar;
      $[39] = dynamicTabsMap;
      $[40] = includeSectionApiData;
      $[41] = includeSectionsData;
      $[42] = t8;
    } else {
      t8 = $[42];
    }
    if ($[43] !== collectionsData || $[44] !== column.background || $[45] !== column.backgroundImage || $[46] !== column.slider || $[47] !== includeSectionApiData) {
      t9 = column.slider && <SliderWidget slider={column.slider} collectionsData={collectionsData} includeSectionApiData={includeSectionApiData} background={column.background} backgroundImage={column.backgroundImage} />;
      $[43] = collectionsData;
      $[44] = column.background;
      $[45] = column.backgroundImage;
      $[46] = column.slider;
      $[47] = includeSectionApiData;
      $[48] = t9;
    } else {
      t9 = $[48];
    }
    t4 = `content-section${column.rows?.some(_temp6) ? " flex-1 flex flex-col" : ""}`;
    if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = {
        contain: "content",
        width: "100%"
      };
      $[49] = t5;
    } else {
      t5 = $[49];
    }
    t6 = column.children_order && column.children_order.length > 0 ? renderOrderedChildren() : renderStandardContent();
    $[0] = collectionsData;
    $[1] = column.background;
    $[2] = column.backgroundImage;
    $[3] = column.children_order;
    $[4] = column.columns;
    $[5] = column.components;
    $[6] = column.heading;
    $[7] = column.horizontalScroll;
    $[8] = column.id;
    $[9] = column.includeSection;
    $[10] = column.justifyContent;
    $[11] = column.pagination;
    $[12] = column.rows;
    $[13] = column.slider;
    $[14] = column.tabBar;
    $[15] = dataStartIndex;
    $[16] = dynamicTabsMap;
    $[17] = includeSectionApiData;
    $[18] = includeSectionsData;
    $[19] = parentColumnData;
    $[20] = priority;
    $[21] = rawDataBindingKey;
    $[22] = getAlignmentClass;
    $[23] = getWidthClass;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    getAlignmentClass = $[22];
    getWidthClass = $[23];
    t4 = $[24];
    t5 = $[25];
    t6 = $[26];
    t7 = $[27];
    t8 = $[28];
    t9 = $[29];
  }
  let t10;
  if ($[50] !== t4 || $[51] !== t5 || $[52] !== t6) {
    t10 = <div className={t4} style={t5}>{t6}</div>;
    $[50] = t4;
    $[51] = t5;
    $[52] = t6;
    $[53] = t10;
  } else {
    t10 = $[53];
  }
  let t11;
  if ($[54] !== t10 || $[55] !== t7 || $[56] !== t8 || $[57] !== t9) {
    t11 = <>{t7}{t8}{t9}{t10}</>;
    $[54] = t10;
    $[55] = t7;
    $[56] = t8;
    $[57] = t9;
    $[58] = t11;
  } else {
    t11 = $[58];
  }
  const innerContent = t11;
  if (skipOuterWrapper) {
    return innerContent;
  }
  const t12 = !!column.tabBar;
  let t13;
  if ($[59] !== column.sticky || $[60] !== column.stickyTop || $[61] !== t12) {
    t13 = getStickyStyles({
      sticky: column.sticky,
      stickyTop: column.stickyTop,
      hasTabBar: t12
    });
    $[59] = column.sticky;
    $[60] = column.stickyTop;
    $[61] = t12;
    $[62] = t13;
  } else {
    t13 = $[62];
  }
  const stickyStyles = t13;
  const t14 = column.background || "transparent";
  const t15 = column.padding || undefined;
  let backgroundStyles;
  if ($[63] !== column.backgroundImage || $[64] !== stickyStyles || $[65] !== t14 || $[66] !== t15) {
    backgroundStyles = {
      backgroundColor: t14,
      padding: t15,
      ...stickyStyles
    };
    if (column.backgroundImage?.enabled && column.backgroundImage?.url) {
      backgroundStyles.backgroundImage = `url(${column.backgroundImage.url})`;
      backgroundStyles.backgroundSize = "cover";
      backgroundStyles.backgroundPosition = "center";
      backgroundStyles.backgroundRepeat = "no-repeat";
    }
    $[63] = column.backgroundImage;
    $[64] = stickyStyles;
    $[65] = t14;
    $[66] = t15;
    $[67] = backgroundStyles;
  } else {
    backgroundStyles = $[67];
  }
  const t16 = `${getWidthClass(column.width)} ${getAlignmentClass()} flex flex-col`;
  let t17;
  if ($[68] !== backgroundStyles || $[69] !== innerContent || $[70] !== t16) {
    t17 = <div className={t16} style={backgroundStyles}>{innerContent}</div>;
    $[68] = backgroundStyles;
    $[69] = innerContent;
    $[70] = t16;
    $[71] = t17;
  } else {
    t17 = $[71];
  }
  return t17;
}
function _temp6(r_0) {
  return r_0.columns?.some(_temp5);
}
function _temp5(c_5) {
  return c_5.sticky;
}
function _temp4(c_3) {
  return c_3.id;
}
function _temp3(c_2) {
  return c_2.type === "nested-column";
}
function _temp2(width) {
  const mobileClass = colSpanMap[width.mobile] || "col-span-12";
  const tabletClass = mdColSpanMap[width.tablet] || "md:col-span-12";
  const desktopClass = lgColSpanMap[width.desktop] || "lg:col-span-12";
  return `${mobileClass} ${tabletClass} ${desktopClass}`;
}
function _temp(item) {
  return !item._isCollectionMeta;
}